'use client';

import { getRoadmapStatusItemHistory } from '@/domain/roadmap/api';
import { type RoadmapStatusItemHistoryDTO } from '@/domain/roadmap/types';
import { Divider } from '@andritzot/metris-web-ui/data-display/divider';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import { Popover } from '@andritzot/metris-web-ui/utils/popover';
import { useQueries } from '@tanstack/react-query';

export interface PendingChange {
  field: string;
  oldValue: string;
  newValue: string;
  changedAt: Date;
  userName: string;
}

export interface ItemHistoryPopoverProps {
  roadmapId: number;
  itemId: number;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  pendingChange?: PendingChange;
}

function PendingChangeSection({ pendingChange }: Readonly<{ pendingChange: PendingChange }>) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>
        Unsaved change
      </Typography>
      <Typography variant="caption" color="text.secondary">
        By: {pendingChange.userName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Field: {pendingChange.field}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {pendingChange.oldValue} → {pendingChange.newValue}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {pendingChange.changedAt.toLocaleString()}
      </Typography>
    </Stack>
  );
}

function SavedHistoryEntry({ entry }: Readonly<{ entry: RoadmapStatusItemHistoryDTO }>) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {entry.user?.name ?? '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {new Date(entry.timestamp).toLocaleString()}
      </Typography>
    </Stack>
  );
}

function SavedHistorySection({
  isLoading,
  entries,
}: Readonly<{ isLoading: boolean; entries: RoadmapStatusItemHistoryDTO[] }>) {
  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading...
      </Typography>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No history found
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        Saved history
      </Typography>
      {entries.map((entry) => (
        <SavedHistoryEntry key={entry.idRoadmapStatusTrackingHistory} entry={entry} />
      ))}
    </Stack>
  );
}

export function ItemHistoryPopover({
  roadmapId,
  itemId,
  anchorEl,
  onClose,
  pendingChange,
}: Readonly<ItemHistoryPopoverProps>) {
  const enabled = !!anchorEl;

  const results = useQueries({
    queries: [
      {
        queryKey: ['roadmap', 'status-item-history', roadmapId, itemId, 'status'],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          getRoadmapStatusItemHistory(roadmapId, itemId, 'status', signal),
        enabled,
        staleTime: 1000 * 60 * 5,
      },
      {
        queryKey: ['roadmap', 'status-item-history', roadmapId, itemId, 'qualityAssurance'],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          getRoadmapStatusItemHistory(roadmapId, itemId, 'qualityAssurance', signal),
        enabled,
        staleTime: 1000 * 60 * 5,
      },
    ],
  });

  const isLoading = results.some((r) => r.isLoading);
  const entries = [...(results[0].data ?? []), ...(results[1].data ?? [])]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 5);

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 1.5, minWidth: 220, maxWidth: 300 }}>
        <Stack spacing={1.5}>
          {pendingChange && <PendingChangeSection pendingChange={pendingChange} />}
          {pendingChange && <Divider />}
          <SavedHistorySection isLoading={isLoading} entries={entries} />
        </Stack>
      </Box>
    </Popover>
  );
}
